import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, googleProvider } from '@config/firebase';
import { USER_ROLES } from '@config/constants';
import { notificationService } from '../services/notificationService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [realUserData, setRealUserData] = useState(null);
  const [impersonatedUser, setImpersonatedUser] = useState(() => {
    try {
      const saved = sessionStorage.getItem('impersonatedUser');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [userDataLoading, setUserDataLoading] = useState(false);
  // true si el doc espejo users/{uid} no se pudo crear → el panel daría
  // errores de permisos, mejor mostrar pantalla de error con recarga.
  const [mirrorFailed, setMirrorFailed] = useState(false);
  // true si el espejo no se creó porque el email no está verificado → hay que
  // mandar al usuario a verificar su email, no a la pantalla de error genérica.
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);

  // userData efectivo: si hay impersonación, ese. Si no, el real.
  const userData = impersonatedUser || realUserData;
  const setUserData = setRealUserData;

  useEffect(() => {
    // Guardia anti-race: si el user cierra sesión mientras estamos cargando
    // sus datos, un `setUserData` tardío podría pisar el `null` del logout.
    // Rastreamos el uid del callback más reciente y solo aplicamos setState
    // si aún somos el "current" en el momento del await.
    let alive = true;
    let currentUid = null;
    const isCurrent = (uid) => alive && currentUid === uid;
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      currentUid = firebaseUser?.uid || null;
      const myUid = currentUid;
      setUser(firebaseUser);
      // Reset de los flags de fallo de espejo en CADA cambio de sesión: si no,
      // un fallo del usuario A dejaría la pantalla de error al entrar el B.
      setMirrorFailed(false);
      setNeedsEmailVerification(false);

      if (firebaseUser) {
        setUserDataLoading(true);
        try {
          // Get user data from Firestore
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('email', '==', firebaseUser.email));
          const userDoc = await getDocs(q);

          if (!userDoc.empty) {
            const data = userDoc.docs[0].data();

            // Comprobar si el super_admin ha revocado las sesiones de este usuario.
            // Si tokensRevokedAt es posterior al authTime del token, forzamos logout.
            try {
              const revokedMs = data.tokensRevokedAt?.toMillis?.() || 0;
              if (revokedMs > 0) {
                const tokenResult = await firebaseUser.getIdTokenResult();
                const authTimeMs = new Date(tokenResult.authTime).getTime();
                if (authTimeMs < revokedMs) {
                  console.warn('Sesión revocada por super_admin — cerrando');
                  try {
                    ['superAdminMode', 'orgSelected', 'sidebar-sections-collapsed']
                      .forEach(k => localStorage.removeItem(k));
                    ['impersonatedUser', 'mesaUser', 'pendingTierInvoice']
                      .forEach(k => sessionStorage.removeItem(k));
                  } catch {}
                  await signOut(auth);
                  setUserData(null);
                  setUserDataLoading(false);
                  setLoading(false);
                  return;
                }
              }
            } catch (e) {
              // No bloqueamos el login si esta check falla
              console.warn('No se pudo comprobar tokensRevokedAt:', e);
            }

            if (!isCurrent(myUid)) return; // stale callback: el user cambió mientras cargábamos
            setUserData({ id: userDoc.docs[0].id, ...data });

            // Sincronizar doc espejo con ID = auth.uid para que las reglas de
            // Firestore funcionen. Lo crea una Cloud Function con el rol REAL
            // del doc canónico (el cliente NO puede auto-asignarse el rol).
            // Reintentamos hasta 3 veces: si el espejo no se crea, TODAS las
            // escrituras del usuario fallarían con permission-denied.
            if (userDoc.docs[0].id !== firebaseUser.uid) {
              const fn = httpsCallable(functions, 'ensureUserMirror');
              let mirrorOk = false;
              let needsVerification = false;
              for (let attempt = 0; attempt < 3 && !mirrorOk && !needsVerification; attempt++) {
                try {
                  const res = await fn();
                  // ok:true (creado o ya existía) o no-canonical-doc → no reintentar
                  if (res?.data?.ok || res?.data?.reason === 'no-canonical-doc') {
                    mirrorOk = true;
                  } else if (res?.data?.reason === 'email-not-verified') {
                    // El usuario debe verificar su email antes de poder operar.
                    // No reintentamos, pero tampoco lo dejamos entrar al panel:
                    // sus escrituras fallarían. Marcamos mirrorFailed para que
                    // App.jsx muestre la pantalla de error con opción de logout.
                    needsVerification = true;
                  }
                } catch (e) {
                  console.warn(`ensureUserMirror intento ${attempt + 1} falló:`, e);
                  if (attempt < 2) await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
                }
              }
              if (!mirrorOk) {
                if (needsVerification) {
                  // No es un fallo técnico: el usuario debe verificar su email.
                  // App.jsx lo mandará al EmailVerificationGate (no a la
                  // pantalla de error genérica, donde "recargar" no arreglaría nada).
                  console.warn('ensureUserMirror: email sin verificar — el usuario debe verificarlo');
                  setNeedsEmailVerification(true);
                } else {
                  console.error('ensureUserMirror falló tras 3 intentos — el panel puede dar errores de permisos');
                  setMirrorFailed(true);
                }
              } else {
                setMirrorFailed(false);
              }
            }
          } else {
            if (isCurrent(myUid)) setUserData(null);
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          if (isCurrent(myUid)) setUserData(null);
        }
        if (isCurrent(myUid)) setUserDataLoading(false);
      } else {
        setUserData(null);
      }
      if (isCurrent(myUid)) setLoading(false);
    });
    return () => { alive = false; unsubscribe(); };
  }, []);

  // Reporta un intento de login fallido al backend (fire & forget).
  // Si se alcanza el umbral, el backend dispara alerta ops.
  const reportFailedLogin = (email, reason) => {
    try {
      const fn = httpsCallable(functions, 'trackFailedLogin');
      fn({ email, reason }).catch(() => {});
    } catch {}
  };

  const loginWithEmail = async (email, password) => {
    const emailLower = email.toLowerCase().trim();

    // 1. Autenticar PRIMERO contra Firebase Auth.
    //    Ya NO creamos cuentas aquí: las cuentas de acceso las crea siempre el
    //    backend (adminInviteUser / signUpFreeOrg / convertLeadToClient) y el
    //    usuario establece su contraseña con el enlace del email de invitación.
    //    Crear cuentas en el login permitía a un atacante "reclamar" el email
    //    de un usuario que aún no hubiera entrado nunca.
    try {
      await signInWithEmailAndPassword(auth, emailLower, password);
    } catch (authError) {
      const code = authError.code || '';
      // Firebase devuelve 'auth/invalid-credential' tanto si el usuario no
      // existe como si la contraseña es incorrecta (anti-enumeración). Damos
      // un mensaje genérico para no revelar qué emails están registrados.
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' ||
          code === 'auth/user-not-found') {
        reportFailedLogin(emailLower, code || 'invalid-credentials');
        throw new Error('invalid-credentials');
      }
      reportFailedLogin(emailLower, code || 'auth-error');
      throw authError;
    }

    // 2. Ya autenticados: cargar el doc del usuario.
    let userDataDoc = null;
    let userDocRef = null;
    try {
      const q = query(collection(db, 'users'), where('email', '==', emailLower));
      const userDoc = await getDocs(q);
      if (!userDoc.empty) {
        userDataDoc = userDoc.docs[0].data();
        userDocRef = userDoc.docs[0].ref;
      }
    } catch { /* no crítico — onAuthStateChanged cargará userData igualmente */ }

    // 3. Si el usuario está rechazado, cerrar la sesión (coherente con
    //    loginWithGoogle, que ya lo hacía). No dejar la sesión Firebase abierta.
    if (userDataDoc?.status === 'rejected') {
      await signOut(auth);
      throw new Error('user-rejected');
    }

    // 4. Registrar actividad (no crítico).
    if (userDocRef) {
      try {
        await updateDoc(userDocRef, {
          lastLoginAt: serverTimestamp(),
          loginCount: (userDataDoc.loginCount || 0) + 1,
        });
      } catch { /* no crítico */ }
    }
    return userDataDoc;
  };

  const loginWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    const email = result.user.email.toLowerCase();

    // Buscar si el usuario existe en Firestore
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const userDoc = await getDocs(q);

    if (userDoc.empty) {
      // Usuario no existe - crear solicitud pendiente
      await addDoc(collection(db, 'users'), {
        email: email,
        name: result.user.displayName || email.split('@')[0],
        role: 'viewer',
        organizationId: '',
        status: 'pending',
        active: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return result;
    }

    const userDataDoc = userDoc.docs[0].data();
    const userDocRef = userDoc.docs[0].ref;

    // Si está rechazado, denegar acceso
    if (userDataDoc.status === 'rejected') {
      await signOut(auth);
      throw new Error('user-rejected');
    }

    // Tracking de actividad. Las cuentas se crean ya como 'approved' desde el
    // backend (adminInviteUser), así que no hay activación de "invited" aquí.
    try {
      await updateDoc(userDocRef, {
        lastLoginAt: serverTimestamp(),
        loginCount: (userDataDoc.loginCount || 0) + 1,
      });
    } catch { /* no crítico */ }

    return result;
  };

  const logout = async () => {
    // Eliminar el token FCM de este dispositivo: si no, el usuario seguiría
    // recibiendo push tras cerrar sesión hasta que FCM lo invalidara solo.
    try {
      const uid = user?.uid;
      const token = notificationService.getCurrentToken?.();
      if (uid && token) {
        await notificationService.removeToken(uid, token);
      }
    } catch { /* no crítico */ }

    // Limpiar TODO el estado persistente para que el próximo usuario que
    // entre por este navegador no herede nada del anterior:
    //  - impersonación super_admin
    //  - modo APP/CRM del super_admin
    //  - organización activa seleccionada
    //  - secciones del sidebar colapsadas
    //  - usuario mesa (viewer en tablet)
    //  - tier invoice pendiente
    // Se conservan: theme y language (UI, no son sensibles).
    try {
      ['superAdminMode', 'orgSelected', 'sidebar-sections-collapsed']
        .forEach(k => localStorage.removeItem(k));
    } catch {}
    try {
      ['impersonatedUser', 'mesaUser', 'pendingTierInvoice']
        .forEach(k => sessionStorage.removeItem(k));
    } catch {}
    setImpersonatedUser(null);
    return signOut(auth);
  };

  // Registro de auditoría de impersonación. Lo escribe una Cloud Function con
  // la identidad REAL del token (no datos que controle el cliente), así el log
  // es fiable. Best-effort: si falla no bloquea la acción.
  const logImpersonation = (action, targetUser) => {
    try {
      const fn = httpsCallable(functions, 'logImpersonation');
      fn({
        action, // 'start' | 'stop'
        targetUserId: targetUser?.id || '',
        targetUserEmail: targetUser?.email || '',
        targetUserRole: targetUser?.role || '',
        targetOrganizationId: targetUser?.organizationId || '',
      }).catch(() => {});
    } catch {}
  };

  const impersonateUser = (targetUser) => {
    // Solo super_admin puede impersonar
    if (realUserData?.role !== 'super_admin') return;
    const clean = {
      id: targetUser.id,
      email: targetUser.email || '',
      name: targetUser.name || '',
      role: targetUser.role || 'viewer',
      organizationId: targetUser.organizationId || '',
      tournamentId: targetUser.tournamentId || '',
      active: true,
      status: 'approved',
      _impersonated: true
    };
    sessionStorage.setItem('impersonatedUser', JSON.stringify(clean));
    setImpersonatedUser(clean);
    logImpersonation('start', clean);
  };

  const stopImpersonating = () => {
    logImpersonation('stop', impersonatedUser);
    sessionStorage.removeItem('impersonatedUser');
    setImpersonatedUser(null);
  };

  const hasPermission = (requiredLevel) => {
    if (!userData) return false;
    const userLevel = USER_ROLES[userData.role]?.level || 0;
    return userLevel >= requiredLevel;
  };

  const isAdminRole = userData?.role === 'super_admin';
  const isOrgAdminRole = hasPermission(50);
  const isRefereeRole = hasPermission(20);

  const isApproved = isAdminRole ||
    isOrgAdminRole ||
    userData?.status === 'approved' ||
    userData?.active === true;

  const isRealSuperAdmin = realUserData?.role === 'super_admin';
  const isImpersonating = !!impersonatedUser;

  const value = useMemo(() => ({
    user,
    userData,
    realUserData,
    loading: loading || userDataLoading,
    loginWithEmail,
    loginWithGoogle,
    logout,
    hasPermission,
    isAdmin: isAdminRole,
    isOrgAdmin: isOrgAdminRole,
    isReferee: isRefereeRole,
    isApproved,
    // Impersonación
    isRealSuperAdmin,
    isImpersonating,
    impersonateUser,
    stopImpersonating,
    // true si no se pudo crear el doc espejo (panel daría permission-denied)
    mirrorFailed,
    // true si el espejo no se creó por email sin verificar → ir al gate
    needsEmailVerification,
  }), [user, userData, realUserData, loading, userDataLoading, isAdminRole, isOrgAdminRole, isRefereeRole, isApproved, isRealSuperAdmin, isImpersonating, mirrorFailed, needsEmailVerification]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
